import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileLines, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { dismissLabraChatPageContext } from '../../../modules/labraChat/actions'
import { getChatRequestContext } from '../../../modules/labraChat/chatRequestContext'
import { LabraChatState } from '../../../modules/labraChat/reducer'
import { useStyles } from './PageContextPill.styles'

/** Copy from the design; the page name itself comes from the host. */
const SUBTITLE = 'Conversation will use this page for context'

interface PageContextPillProps {
  /** Human-readable name of the page the answers will draw on. */
  label?: string
}

/**
 * Tells the user, before they send, that the agent can see the page they are on
 * — and lets them take that back.
 *
 * Shown only while the context would actually be sent: it disappears once the
 * first message goes out (context is a one-shot per conversation) and once the
 * user dismisses it.
 */
export const PageContextPill: React.FC<PageContextPillProps> = ({ label }) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const isDismissed = useSelector(
    (state: { labraChat?: LabraChatState }) =>
      state.labraChat?.isPageContextDismissed ?? false
  )
  const isSent = useSelector(
    (state: { labraChat?: LabraChatState }) =>
      state.labraChat?.isPageContextSent ?? false
  )

  // No label means the host could not name this page; say nothing rather than
  // claim context the user cannot identify. No provider means there is no context
  // to send either, and promising one would be a lie.
  if (!label || isDismissed || isSent) return null
  if (getChatRequestContext() === null) return null

  return (
    <div className={classes.pill} data-testid="page-context-pill">
      <span className={classes.iconTile}>
        <FontAwesomeIcon icon={faFileLines} className={classes.icon} />
      </span>
      <span className={classes.text}>
        <Typography component="span" className={classes.title} title={label}>
          {label}
        </Typography>
        <Typography component="span" className={classes.subtitle}>
          {SUBTITLE}
        </Typography>
      </span>
      <button
        type="button"
        className={classes.dismiss}
        aria-label="Don't use this page"
        data-testid="page-context-dismiss"
        onClick={() => dispatch(dismissLabraChatPageContext())}
      >
        <FontAwesomeIcon icon={faXmark} className={classes.dismissIcon} />
      </button>
    </div>
  )
}
